import React, { useMemo } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Heart,
  Puzzle,
  Shapes,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { SHAPES } from "../lib/sewingShapes";
import { COMPOSITES, LESSONS, loadProgress } from "../lib/sewingCurriculum";
import { labHref } from "../lib/sewingLinks";
import SewingShell from "../components/sewing/SewingShell";

/* ============================================================
   /sewing-primitives — the hub. The thesis, where you left off,
   and doors into the three rooms: the lab (play with the
   primitives), learn (the curriculum), make (compose objects).

   This page grew everything on one scroll for four PRs; now
   it's an overview and the rooms carry the content.
   ============================================================ */

// One room door: icon, name, what's inside, and a call to action.
const RoomCard = ({ to, Icon, name, blurb, cta, children }) => (
  <Card className="flex flex-col bg-card/60 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg">
        <Icon className="h-5 w-5 text-primary" />
        {name}
      </CardTitle>
      <CardDescription>{blurb}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col gap-3">
      {children}
      <Link
        to={to}
        className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        {cta}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </CardContent>
  </Card>
);

const SewingPrimitives = () => {
  // Old deep links pointed at this page (?shape=cylinder&radius=1.2...);
  // the lab owns those now. Forward the whole query string.
  const [searchParams] = useSearchParams();
  const legacyShape = searchParams.get("shape");

  const progress = useMemo(loadProgress, []);
  const totalChecks = LESSONS.reduce((n, l) => n + l.checks.length, 0);
  const doneChecks = LESSONS.reduce(
    (n, l) => n + l.checks.filter((c) => progress[c.id]).length,
    0
  );
  const nextLesson = LESSONS.find((l) => l.checks.some((c) => !progress[c.id]));
  const nextIndex = nextLesson ? LESSONS.indexOf(nextLesson) : -1;

  if (legacyShape) {
    return <Navigate to={`/sewing-primitives/lab?${searchParams.toString()}`} replace />;
  }

  return (
    <SewingShell
      active="overview"
      title="Sewing Primitives"
      intro={
        <>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Learning to sew 3D objects the way 3D modeling teaches form:
            primitives first. A pattern is just a shape unfolded flat — so
            here, every primitive is shown both ways at once, and you can
            watch one become the other.
          </p>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            The bet: if the fold from flat pattern to 3D form becomes
            intuitive for a handful of primitives, then any object — a plush
            microwave, a tiny armchair, whatever — is just primitives
            composed. Sliders change the shape; the pattern math updates
            live, because the pattern <em>is</em> the math.
          </p>
        </>
      }
    >
      {/* ---- where you left off ---- */}
      <Card className="mt-10 bg-card/80 backdrop-blur">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-5">
          <div>
            <p className="text-base font-semibold">
              {doneChecks === 0
                ? "Start at the beginning"
                : nextLesson
                ? "Pick up where you left off"
                : "Curriculum complete 🎉"}
            </p>
            <p className="text-sm text-muted-foreground">
              {doneChecks === 0
                ? `${LESSONS.length} lessons, from "what is a seam" to "plush anything."`
                : nextLesson
                ? `${doneChecks}/${totalChecks} checks done — next: lesson ${nextIndex}, ${nextLesson.title}.`
                : `All ${totalChecks} checks done. The make page awaits your victory lap.`}
            </p>
          </div>
          <Link
            to="/sewing-primitives/learn"
            className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow transition-all hover:opacity-90"
          >
            <BookOpen className="h-4 w-4" />
            {doneChecks === 0 ? "Start learning" : "Continue"}
          </Link>
        </CardContent>
      </Card>

      {/* ---- the rooms ---- */}
      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <RoomCard
          to="/sewing-primitives/lab"
          Icon={Shapes}
          name="The lab"
          blurb="Every primitive as a flat pattern that folds into its 3D shape — scrub the fold, stretch the sliders."
          cta="open the lab"
        >
          <div className="flex flex-wrap gap-1.5">
            {SHAPES.map((s) => (
              <Link
                key={s.id}
                to={labHref(s.id)}
                className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                {s.name}
              </Link>
            ))}
          </div>
        </RoomCard>

        <RoomCard
          to="/sewing-primitives/learn"
          Icon={BookOpen}
          name="Learn"
          blurb={`${LESSONS.length} lessons in dependency order, with hands-on checks that save on this device.`}
          cta="open the curriculum"
        >
          <p className="text-sm text-muted-foreground">
            Seams → boxes → curves → gores → darts → soft shapes → joints →
            composing anything. Plus the standing list of places where fabric
            disagrees with math.
          </p>
        </RoomCard>

        <RoomCard
          to="/sewing-primitives/make"
          Icon={Puzzle}
          name="Make"
          blurb="The assembly lab, a starter menu of seven objects with generated cut lists, and the creature generator."
          cta="make something"
        >
          <div className="flex flex-wrap items-center gap-1.5 text-xl">
            {COMPOSITES.map((c) => (
              <Link
                key={c.id}
                to={`/sewing-primitives/make/${c.id}`}
                title={c.name}
                className="transition-transform hover:-translate-y-0.5"
              >
                <span aria-hidden>{c.emoji}</span>
                <span className="sr-only">{c.name}</span>
              </Link>
            ))}
            <Link
              to="/sewing-primitives/creature"
              title="Creature generator"
              className="transition-transform hover:-translate-y-0.5"
            >
              <span aria-hidden>🪄</span>
              <span className="sr-only">Creature generator</span>
            </Link>
          </div>
        </RoomCard>
      </div>

      {/* ---- the flagship demo teaser ---- */}
      <Link
        to="/sewing-primitives/make/teddy"
        className="mt-6 flex flex-col gap-2 rounded-xl border bg-card/50 p-5 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/50 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h3 className="flex items-center gap-2 font-semibold">
            <Heart className="h-5 w-5 text-primary" />
            The one to stare at: the teddy
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Spheres and cylinders wearing a trench coat — pull it apart in 3D
            and every joint type shows itself. The whole thesis in one bear.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary">
          see the bear
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </Link>

      <p className="mt-14 text-center text-sm text-muted-foreground">
        Status: I have touched a sewing machine once. This site is the theory
        getting ahead of the thread — on purpose. 🧵
      </p>
    </SewingShell>
  );
};

export default SewingPrimitives;
